import { FC, Fragment } from 'react';
import CombinedIcon from './CombinedIcon';
import { TCommentIcon } from './EditorCommentsContainer';
import SingleIcon from './SingleIcon';
import { NEW_COMMENT_ID } from '@modules/comments/constants';

// If 4+ comments on the same line => single icon
const MULTIPLE_COMMENTS_THRESHOLD = 4;

// Max vertical distance (px) to treat two icons as "same line"
const NEARBY_LINE_MERGE_THRESHOLD = 3; // adjust as needed

type TLineGroup = {
  topPos: number; // canonical (lowest) topPos for this visual line
  icons: TCommentIcon[];
};

const mergeIconsIntoLines = (commentIcons: TCommentIcon[]): TLineGroup[] => {
  if (commentIcons.length === 0) return [];

  // Sort icons by topPos (ascending)
  const sorted = [...commentIcons].sort((a, b) => a.topPos - b.topPos);

  const groups: TLineGroup[] = [];

  for (const icon of sorted) {
    const lastGroup = groups[groups.length - 1];

    if (!lastGroup) {
      // First group
      groups.push({ topPos: icon.topPos, icons: [icon] });
      continue;
    }

    // If icon is close enough vertically, merge into the last group
    if (icon.topPos - lastGroup.topPos <= NEARBY_LINE_MERGE_THRESHOLD) {
      lastGroup.icons.push(icon);
      // we keep lastGroup.topPos as the *lower* (first) topPos → canonical
    } else {
      // Start a new group / visual line
      groups.push({ topPos: icon.topPos, icons: [icon] });
    }
  }

  return groups;
};

type TProps = {
  commentIcons: TCommentIcon[];
};

/**
 * The overlay that renders icon(s) for each (visual) line with comments.
 */
const IconsOverlay = ({ commentIcons }: TProps) => {
  // Merge icons that are very close vertically into the same "line"
  const mergedLines = mergeIconsIntoLines(commentIcons);

  return (
    <>
      {mergedLines.map(({ topPos, icons }) => {
        const iconsOnLine = icons;

        if (iconsOnLine.length >= MULTIPLE_COMMENTS_THRESHOLD) {
          let newIcon: TCommentIcon | undefined;
          const withoutNewComment: TCommentIcon[] = [];

          for (const icon of iconsOnLine) {
            if (icon.id === NEW_COMMENT_ID) {
              newIcon = icon;
            } else if (!icon.isStale) {
              withoutNewComment.push(icon);
            }
          }

          return (
            <Fragment key={iconsOnLine.map((icon) => icon.id).join('-')}>
              <CombinedIcon topPos={topPos} lineCommentIcons={withoutNewComment} />
              {newIcon && <SingleIcon topPos={topPos} icon={newIcon} index={1} />}
            </Fragment>
          );
        }

        return iconsOnLine.map(
          (icon, index) => !icon.isStale && <SingleIcon key={icon.id} topPos={topPos} icon={icon} index={index} />
        );
      })}
    </>
  );
};

export default IconsOverlay;
